import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
  listCustomers,
  listQuotes,
  listProducts,
  searchCustomersWithDebounce,
  searchProductsWithDebounce,
  generateCustomerInvoice,
  getCustomerInvoiceByServiceReportId,
  associateQuoteToService,
  updateEstimateStatus,
  ListCustomersParams,
  ListQuotesParams,
  ListProductsParams,
  AssociateQuoteToServiceRequest,
} from "@/_services/invoiceService";
import { TCustomQueryProps } from "@/common/type/hooks.types";

export const useGetCustomers = (params: ListCustomersParams = {}) => {
  return useQuery({
    queryKey: ["billing", "customers", params],
    queryFn: () => listCustomers(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true,
  });
};

export const useSearchCustomers = (
  searchQuery: string,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: ["billing", "customers", "search", searchQuery],
    queryFn: () => searchCustomersWithDebounce(searchQuery),
    enabled: (options.enabled ?? true) && searchQuery.trim().length >= 2,
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGetCustomersInfinite = (
  baseParams: Omit<ListCustomersParams, "page"> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["billing", "customers", "infinite", baseParams],
    queryFn: ({ pageParam = 1 }) =>
      listCustomers({ ...baseParams, page: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      return lastPage.has_more ? pages.length + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useGetQuotes = (params: ListQuotesParams = {}) => {
  return useQuery({
    queryKey: ["billing", "quotes", params],
    queryFn: () => listQuotes(params),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    keepPreviousData: true,
  });
};

export const useGetFilteredQuotes = (status: string) => {
  const params: ListQuotesParams = {
    filter: [{ field: "status", operator: "scope", value: status }],
  };

  return useQuery({
    queryKey: ["billing", "quotes", status],
    queryFn: () => listQuotes(params),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    keepPreviousData: true,
  });
};

export const useGetQuotesInfinite = (
  baseParams: Omit<ListQuotesParams, "page"> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["billing", "quotes", "infinite", baseParams],
    queryFn: ({ pageParam = 1 }) =>
      listQuotes({ ...baseParams, page: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      return pages.length < lastPage.total_pages ? pages.length + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useGetProducts = (params: ListProductsParams = {}) => {
  return useQuery({
    queryKey: ["billing", "products", params],
    queryFn: () => listProducts(params),
    staleTime: 10 * 60 * 1000, // Products change less frequently
    cacheTime: 15 * 60 * 1000,
    keepPreviousData: true,
  });
};

export const useGetProductsInfinite = (
  baseParams: Omit<ListProductsParams, "cursor"> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["billing", "products", "infinite", baseParams],
    queryFn: ({ pageParam }) =>
      listProducts({ ...baseParams, cursor: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage.has_more ? lastPage.next_cursor : undefined;
    },
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  });
};

export const useSearchProducts = (
  searchQuery: string,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: ["billing", "products", "search", searchQuery],
    queryFn: () => searchProductsWithDebounce(searchQuery),
    enabled: (options.enabled ?? true) && searchQuery.trim().length >= 1,
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGenerateCustomerInvoice = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serviceReportId: string) =>
      generateCustomerInvoice(serviceReportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });

      handleSuccess?.();
    },
    onError: (error: AxiosError) => {
      handleError?.(error);
      console.error("Error when generating customer invoice:", error?.message);
    },
  });
};

export const useGetCustomerInvoiceByServiceReportId = (
  serviceReportId: string
) => {
  return useQuery({
    queryKey: ["billing", "invoices", "serviceReport", serviceReportId],
    queryFn: () => getCustomerInvoiceByServiceReportId(serviceReportId),
    enabled: !!serviceReportId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useAssociateQuoteToService = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssociateQuoteToServiceRequest) =>
      associateQuoteToService(data),
    onSuccess: (data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["billing", "invoices"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "customers"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "quotes"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({
        queryKey: ["getServiceById", variables.serviceId],
      });

      handleSuccess?.(data);
    },
    onError: (error: AxiosError) => {
      console.error("Error when associating quote to service:", error?.message);
      handleError?.(error);
    },
  });
};

export interface IUpdateEstimateStatus {
  quoteId: string;
  serviceId: string;
  status: "accepted" | "denied";
}

export const useUpdateEstimateStatus = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps<IUpdateEstimateStatus>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IUpdateEstimateStatus) =>
      updateEstimateStatus(data.quoteId, data.serviceId, data.status),
    onSuccess: (_, variables) => {
      const { serviceId } = variables;

      queryClient.invalidateQueries({
        queryKey: ["getServiceById", serviceId],
      });
      queryClient.invalidateQueries({ queryKey: ["billing", "quotes"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });

      handleSuccess?.(variables);
    },
    onError: (error: AxiosError) => {
      handleError?.(error);
      console.error("Error when updating estimate status:", error?.message);
    },
  });
};

export const usePrefetchBillingData = () => {
  const queryClient = useQueryClient();

  const prefetchCustomers = (params: ListCustomersParams = {}) => {
    return queryClient.prefetchQuery({
      queryKey: ["billing", "customers", params],
      queryFn: () => listCustomers(params),
      staleTime: 5 * 60 * 1000,
    });
  };

  const prefetchQuotes = (params: ListQuotesParams = {}) => {
    return queryClient.prefetchQuery({
      queryKey: ["billing", "quotes", params],
      queryFn: () => listQuotes(params),
      staleTime: 5 * 60 * 1000,
    });
  };

  const prefetchProducts = (params: ListProductsParams = {}) => {
    return queryClient.prefetchQuery({
      queryKey: ["billing", "products", params],
      queryFn: () => listProducts(params),
      staleTime: 10 * 60 * 1000,
    });
  };

  return {
    prefetchCustomers,
    prefetchQuotes,
    prefetchProducts,
  };
};

export const useInvalidateBillingData = () => {
  const queryClient = useQueryClient();

  const invalidateCustomers = () => {
    return queryClient.invalidateQueries({
      queryKey: ["billing", "customers"],
    });
  };

  const invalidateQuotes = () => {
    return queryClient.invalidateQueries({ queryKey: ["billing", "quotes"] });
  };

  const invalidateProducts = () => {
    return queryClient.invalidateQueries({ queryKey: ["billing", "products"] });
  };

  const invalidateAll = () => {
    return queryClient.invalidateQueries({ queryKey: ["billing"] });
  };

  return {
    invalidateCustomers,
    invalidateQuotes,
    invalidateProducts,
    invalidateAll,
  };
};
